import {
  STAMP_KINDS,
  isInkTool,
  isShapeTool,
  type AnnotationView,
  type LiveChunk,
  type LiveTool,
  type StampKind,
  type Stroke,
  type StrokeTool,
} from './annotation-geometry'

/**
 * Annotation on the room channel — what `useAnnotations` sends and how what
 * arrives is checked before it reaches the store.
 *
 * Events (see RoomChannel): `annotation:stroke` / `:live` / `:erase` /
 * `:clear` / `:get` out; `annotation:stroke` / `:live` / `:erased` and
 * `share:view` in. Every payload names its presentation as `shareId`.
 *
 * Everything inbound is treated as untrusted shape: the server validates what
 * it stores, but a mark that would throw while being drawn must never reach
 * the renderer, whatever sent it.
 */

const isStrokeTool = (v: unknown): v is StrokeTool =>
  isInkTool(v) || isShapeTool(v) || v === 'text' || v === 'stamp'
const isLiveTool = (v: unknown): v is LiveTool => isInkTool(v) || isShapeTool(v) || v === 'laser'
const isUnit = (v: unknown) => typeof v === 'number' && v >= 0 && v <= 1
const isPoints = (v: unknown): v is number[] =>
  Array.isArray(v) && v.length >= 2 && v.length % 2 === 0 && v.every(isUnit)
const isColor = (v: unknown): v is string => typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v)
const isPage = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 1
const isStamp = (v: unknown): v is StampKind => STAMP_KINDS.includes(v as StampKind)

/** The number of points each kind carries; null for ink, which carries any. */
const pointCount = (tool: StrokeTool | LiveTool): number | null =>
  isShapeTool(tool) ? 2 : tool === 'text' || tool === 'stamp' ? 1 : null

const pointsFit = (tool: StrokeTool | LiveTool, points: number[]) => {
  const n = pointCount(tool)
  return n === null || points.length === n * 2
}

/** A finished mark off the wire, or null. */
export function readStroke(v: unknown): Stroke | null {
  const s = v as Partial<Stroke> | null
  if (
    !s ||
    typeof s.id !== 'string' ||
    !s.id ||
    typeof s.by !== 'string' ||
    !isPage(s.page) ||
    !isStrokeTool(s.tool) ||
    !isColor(s.color) ||
    typeof s.width !== 'number' ||
    !(s.width > 0) ||
    !isPoints(s.points) ||
    !pointsFit(s.tool, s.points)
  ) {
    return null
  }
  const stroke: Stroke = { id: s.id, by: s.by, page: s.page, tool: s.tool, color: s.color, width: s.width, points: s.points }
  if (s.tool === 'text') {
    if (typeof s.text !== 'string' || !s.text) return null
    stroke.text = s.text
  }
  if (s.tool === 'stamp') {
    if (!isStamp(s.stamp)) return null
    stroke.stamp = s.stamp
  }
  return stroke
}

/** A live slice off the wire, with its author, or null. */
export function readLive(v: unknown): { shareId: string; by: string; chunk: LiveChunk } | null {
  const m = v as Partial<LiveChunk> & { shareId?: unknown; by?: unknown } | null
  if (
    !m ||
    typeof m.shareId !== 'string' ||
    typeof m.by !== 'string' ||
    typeof m.id !== 'string' ||
    !isPage(m.page) ||
    !isLiveTool(m.tool) ||
    !isColor(m.color) ||
    typeof m.width !== 'number' ||
    !(m.width > 0) ||
    typeof m.from !== 'number' ||
    !Number.isInteger(m.from) ||
    m.from < 0 ||
    !isPoints(m.points) ||
    !pointsFit(m.tool, m.points)
  ) {
    return null
  }
  return {
    shareId: m.shareId,
    by: m.by,
    chunk: { id: m.id, page: m.page, tool: m.tool, color: m.color, width: m.width, from: m.from, points: m.points },
  }
}

/** The presenter's position off the wire, or null. */
export function readView(v: unknown): AnnotationView | null {
  const m = v as Partial<AnnotationView> | null
  if (!m || !isPage(m.page)) return null
  return { page: m.page, offset: isUnit(m.offset) ? (m.offset as number) : 0 }
}

/** A mark as sent: without `by` — the server stamps the sender. */
export function toWireStroke({ id, page, tool, color, width, points, text, stamp }: Stroke): Omit<Stroke, 'by'> {
  return {
    id,
    page,
    tool,
    color,
    width,
    points,
    ...(tool === 'text' ? { text } : {}),
    ...(tool === 'stamp' ? { stamp } : {}),
  }
}
